import React, { useEffect, useState, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css'
import './AuthForm.css';
import { FaCheck, FaXmark, FaEye } from 'react-icons/fa6';
import Preloader from './Preloader';
import { useNavigate } from 'react-router-dom';

function IDVerification() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Modal for detail view
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationMsg, setVerificationMsg] = useState('');

  const navigate = useNavigate();
  const auditLoggedRef = useRef({});
  const [auditLoggedUserId, setAuditLoggedUserId] = useState(null);

  useEffect(() => {
    document.title = "ID Verification";
    fetchUsers();
    getCurrentUser();
  }, []);

  async function getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      setCurrentUser(profile);
    }
  }

  async function fetchUsers() {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      setError('Failed to load users');
      setUsers([]);
    } else {
      setUsers(data);
    }
    setLoading(false);
  }

  // Filtering, searching, and pagination logic
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch =
        search.trim() === '' ||
        ((user.first_name && user.first_name.toLowerCase().includes(search.toLowerCase())) ||
         (user.last_name && user.last_name.toLowerCase().includes(search.toLowerCase())) ||
         (user.email && user.email.toLowerCase().includes(search.toLowerCase())));
      const matchesStatus =
        statusFilter === 'all' ||
        (user.id_verification_status && user.id_verification_status.toLowerCase() === statusFilter);
      return matchesSearch && matchesStatus;
    });
  }, [users, search, statusFilter]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    return filteredUsers.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  }, [filteredUsers, page, itemsPerPage]);

  useEffect(() => {
    // Reset to first page if filter/search changes
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    async function logAudit() {
      if (
        selectedUser &&
        currentUser &&
        (currentUser.role === 'admin' || currentUser.role === 'super admin') &&
        selectedUser.id !== auditLoggedUserId
      ) {
        await supabase.from('audit_logs').insert([
          {
            user_id: currentUser.id,
            action: 'view ID',
            target_id: selectedUser.id,
            target_type: 'VerifyID',
            details: { email: selectedUser.email }
          }
        ]);
        if (!cancelled) setAuditLoggedUserId(selectedUser.id);
      }
    }
    logAudit();
    return () => { cancelled = true; };
  }, [selectedUser, currentUser, auditLoggedUserId]);

  // Guard variables for conditional rendering
  const isLoading = loading || !currentUser;
  const isError = !!error;
  const isDenied = currentUser && currentUser.role !== 'admin' && currentUser.role !== 'super admin';

  if (isLoading) return <Preloader text="Loading..." />;
  if (isError) return <div style={{ color: '#d32f2f', padding: 40 }}>{error}</div>;
  if (isDenied) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar onNarrowChange={setSidebarNarrow}/>
        <div style={{ flex: 1, padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, padding: '32px 48px', fontWeight: 500, fontSize: 20 }}>
          Access denied. You do not have the necessary permissions to view this page.
          </div>
        </div>
      </div>
    );
  }

  if (!selectedUser) {
    // List view
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar onNarrowChange={setSidebarNarrow}/>
        <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
          <div className="header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Mobile Hamburger Menu */}
              <MobileHamburger
                  isOpen={showMobileMenu}
                  onToggle={() => setShowMobileMenu(prev => !prev)}
              />
            <div className='title-text'>ID Verification</div>
            <ProfileMenu />
          </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        </div>
          <div className="search-actions-row">
            <div className="search-filters">
              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search by name or email"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="search-input"
                />
                <span className="search-icon">
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                </span>
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="type-filter">
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: 0, boxShadow: '0 2px 8px #0001', minHeight: 200, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>Avatar</th>
                  <th style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Name</th>
                  <th style={{ padding: 12, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Email</th>
                  <th style={{ padding: 12 }}>ID Status</th>
                  <th style={{ padding: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.length > 0 ? paginatedUsers.map(user => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}>
                      <img
                        src={user.avatar_url || 'https://www.svgrepo.com/show/384674/account-avatar-profile-user-11.svg'}
                        alt="User avatar"
                        style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                      />
                    </td>
                    <td style={{ padding: 12, fontWeight: 600, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.first_name} {user.last_name}</td>
                    <td style={{ padding: 12, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</td>
                    <td style={{ padding: 12 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontWeight: 600,
                        fontSize: 14,
                        background: user.id_verification_status === 'verified' ? '#e6f4ea' :
                          user.id_verification_status === 'submitted' ? '#fff3cd' :
                          user.id_verification_status === 'rejected' ? '#ffebee' :
                          user.id_verification_status === 'pending' ? '#f1f3f4' : '#f1f3f4',
                        color: user.id_verification_status === 'verified' ? '#1e7e34' :
                          user.id_verification_status === 'submitted' ? '#856404' :
                          user.id_verification_status === 'rejected' ? '#c62828' :
                          user.id_verification_status === 'pending' ? '#5f6368' : '#5f6368',
                        border: '1px solid #eee',
                        minWidth: 70,
                        textAlign: 'center'
                      }}>{user.id_verification_status ? user.id_verification_status.charAt(0).toUpperCase() + user.id_verification_status.slice(1) : 'Pending'}</span>
                    </td>
                    <td style={{ padding: 12 }}>
                      <button
                        onClick={() => navigate(`/id-verification/${user.id}`)}
                        style={{
                          background: '#08b1c7',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          padding: '6px 18px',
                          fontWeight: 'bold',
                          fontSize: 14,
                          cursor: 'pointer'
                        }}
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setPage(i + 1)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                    background: page === i + 1 ? '#08b1c7' : '#fff',
                    color: page === i + 1 ? '#fff' : '#333',
                    fontWeight: page === i + 1 ? 'bold' : 'normal'
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
          </div>
        </div>
      </div>
    );
  }

  // Modal for detail view
  if (selectedUser) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#0008',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 32,
          minWidth: 400,
          maxWidth: 600,
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: '0 8px 32px rgba(8, 177, 199, 0.15)'
        }}>
          <button
            onClick={() => setSelectedUser(null)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              background: 'none',
              border: 'none',
              fontSize: 24,
              color: '#08b1c7',
              cursor: 'pointer',
              zIndex: 10
            }}
            aria-label="Close"
          >
            <FaXmark />
          </button>
          {/* ID Verification detail template here, using selectedUser instead of user */}
          {/* ...copy the ID verification detail JSX from ManageUserDetails.js, replacing user with selectedUser, and using verificationLoading/Msg state... */}
        </div>
      </div>
    );
  }
}

export default IDVerification; 